/**
 * RBAC ルートガードの判定ロジック (純関数 / RBAC P1 F10)
 *
 * React に依存しない純関数として切り出し、vitest で単体テスト可能にする。
 * RequirePermission コンポーネントはこの関数を usePermission の結果で呼び出す。
 *
 * 注: FE の判定は UX / defense-in-depth であり、最終的な認可は backend が enforce する。
 */
import type { PermissionArea, PermissionAction, UserRole } from '@/types';

export interface GuardCaps {
  /** 現在のロール (未認証なら null) */
  role: UserRole | null;
  /** 指定エリア・アクションの権限を持つか */
  can: (area: PermissionArea, action: PermissionAction) => boolean;
}

export interface GuardSpec {
  /** ロール制限 (例: 'superadmin')。指定時はこのロールのみ許可 */
  role?: UserRole | UserRole[];
  /** いずれかのエリアに read 権限があれば許可 (例: admin = orgSettings or userMgmt) */
  anyArea?: PermissionArea[];
  /** 単一エリアの権限が必要 */
  area?: PermissionArea;
  /** area / anyArea に対するアクション (既定: read) */
  action?: PermissionAction;
}

/**
 * ガード条件を満たすか判定する。
 * role / anyArea / area は AND で評価し、指定されたものを全て満たす必要がある。
 * いずれも未指定なら許可 (素通し)。
 */
export function isGuardAllowed(caps: GuardCaps, spec: GuardSpec): boolean {
  const action = spec.action ?? 'read';

  if (spec.role) {
    const allowed = Array.isArray(spec.role) ? spec.role : [spec.role];
    if (!caps.role || !allowed.includes(caps.role)) return false;
  }

  if (spec.anyArea && spec.anyArea.length > 0) {
    if (!spec.anyArea.some((a) => caps.can(a, action))) return false;
  }

  if (spec.area) {
    if (!caps.can(spec.area, action)) return false;
  }

  return true;
}
